import { isEmpty } from 'lodash';
import { AskScope } from '../api';

/**
 * A prompt instruction telling the Ask agent to link the cards it names, using the
 * markdown link syntax the transcript renderer understands (see the `[label](href)`
 * branch in `Markdown.tsx`). Answers are rendered as markdown, so an unlinked card is
 * a dead end for the user even though the agent knows exactly which card it means.
 *
 * Cards are the only thing we ask it to link. A collection-view link would have to
 * carry a hand-assembled `collectionCriteria` JSON blob, and a wrong one fails
 * silently — the view just drops the filter it can't resolve (see
 * `mapFilterByComponentToState`) and shows an unfiltered list as if it were the
 * answer. Card links have no such failure mode: one id, copied verbatim, and a wrong
 * one is immediately visible.
 *
 * Every card route needs a channel slug (there is no card-id-only route), so when the
 * scope tells us which channel the user is asking from, the slug is supplied here and
 * the agent only has to fill in the card id. Otherwise both values have to come out of
 * tool output, and it is told to skip the link rather than guess at either one.
 *
 * The link text is the card's name alone, and the two "ids" involved are deliberately kept
 * apart. The URL takes the card's internal uuid. The "WO 1042" style number the agent sees in
 * tool output is the `sequenceNumber` component (`STATIC_COMPONENT_IDS.SEQUENCE_NUMBER`) — a
 * server-managed, readonly `NUMBER` field that counts up and is relabelable per workflow
 * ("Card ID Number" by default). Nothing on the frontend treats it as an identifier; it is
 * filterable and sortable like any other number. Prefixing the name with it reads as though
 * the number were the card's real identity, which the product never claims.
 *
 * Naming the card is a workable default rather than a correct one: a no-code workflow can put
 * any field first, so the field its users identify cards by is not always `name`. See the plan
 * on card identity in Ask answers.
 */
export function cardLinkDirective(scope: AskScope): string {
  const slug = 'channelSlug' in scope ? scope.channelSlug : undefined;
  const pathRule = isEmpty(slug)
    ? 'Build the path as /{channelSlug}/cards/{cardId}, taking BOTH the channel slug ' +
      'and the card id verbatim from tool output. If you do not have both for a card, ' +
      'name it without a link.'
    : `Build the path as /${slug}/cards/{cardId} — cards in this workspace use the ` +
      `channel slug "${slug}" — taking the card id verbatim from tool output. For a card ` +
      "in a different workspace, use that workspace's own slug from tool output, or " +
      'name the card without a link if you do not have it.';

  return [
    "When you name a specific card, link it using markdown link syntax with the card's " +
      'name as the visible text: [Replace hydraulic pump](/maintenance/cards/abc123).',
    pathRule,
    'Never invent, construct, or infer an id or a slug — a wrong link is worse than no link.',
    "The link text is the card's name on its own — never the raw URL, and never the name " +
      'prefixed with a number from one of the card\'s fields ("WO 1042 — Replace hydraulic ' +
      'pump"). A card ID number is an ordinary field that happens to count up; it is not ' +
      'what identifies the card, and the id the URL needs is a separate internal value. If ' +
      'the answer calls for a card ID number, give it its own column instead.',
    "Links work inside table cells. In a table, link the cell holding the card's name " +
      'only, not several cells in the same row. Link a card the first time you name it ' +
      'and not on every later mention.',
    'Link cards only — never collection views, filtered lists, or other app screens. If ' +
      'the answer is a list the user could reproduce with the filter controls, you may ' +
      'say so in words, but do not build a URL for it.',
    'Put links in your markdown answer. Links inside an HTML answer do not work.',
  ].join(' ');
}

/**
 * The counterpart for surfaces where a link is pointless: the summary widgets answer
 * about the card or subform the user is already looking at, so a link would only ever
 * point back at the open context. Said explicitly rather than by omission because the
 * gateway's system prompt is not ours and may ask for links on its own.
 */
export function noCardLinkDirective(): string {
  return (
    'Do not include links of any kind. The user is already looking at the record you are ' +
    'summarizing, so name cards in plain text.'
  );
}
